/**
 * Driver Themes ticket — orb composite fragment shader.
 *
 * One GLSL source serves three variants (Initiative, Electric
 * Light, Dark Electric) via a `#define ORB_KIND` value injected at
 * compile time. Each variant becomes its own compiled program, but
 * the source string ships once and is reused; the injection
 * happens via `prependOrbKind` below so the compile pipeline in
 * Commit 6 doesn't have to know variant names.
 *
 * Branching inside the shader:
 *   - ORB_DARK_ELECTRIC takes the analytic sphere intersection
 *     path (matte black orb with fresnel rim).
 *   - All other ORB_KIND values take the SDF raymarch path (smoky
 *     displaced sphere with diffuse + fresnel + shadow tinting).
 *
 * Per-variant colour and shape uniforms live in `ORB_UNIFORMS`
 * below. The ShaderChargingAnimation component in Commit 7 binds
 * these uniforms after linking the program for the active
 * shaderVariant.
 *
 * Source is verbatim from the ticket spec. Do not refactor GLSL
 * during transcription — every constant, every variable name,
 * every line break is significant for visual reproducibility
 * against the design widget the spec was authored from.
 */
export const ORB_COMPOSITE_SHADER_SOURCE = `#version 300 es
precision highp float;
#define ORB_INITIATIVE     0
#define ORB_ELECTRIC_LIGHT 1
#define ORB_DARK_ELECTRIC  2
// ORB_KIND injected via prepend:
// #define ORB_KIND ORB_INITIATIVE
//
// VOLUMETRIC_STEPS controls the background raymarch loop count.
// Defaults to 32 if not injected. Commit 11 of the Driver Themes
// ticket conditionally injects 24 on low-end devices
// (navigator.hardwareConcurrency < 4) for ~25% GPU savings on this
// pass. #ifndef guard lets the injection override cleanly without
// the GLSL "macro redefined" warning that a bare #define would
// produce when the injected value differs from the default.
#ifndef VOLUMETRIC_STEPS
#define VOLUMETRIC_STEPS 32
#endif
uniform float iTime;
uniform vec2  iResolution;
uniform float uIntensity;
uniform float uKwhScale;
// Swipe-to-spin interaction angles (radians), vec2: uSpin.x = yaw (horizontal
// swipe, about the vertical axis), uSpin.y = pitch (vertical swipe, about the
// horizontal axis). Both 0 at baseline (the JS impulse decays back to 0), so
// the rotation below is an identity no-op until a swipe imparts spin. Separate
// from uKwhScale, so growth is untouched.
uniform vec2 uSpin;
// Tap-to-chaos impulse for Dark Electric only (its smooth sphere can't show
// spin). 0 at rest (identity); a tap sets it to 1 and it decays back, breaking
// the sphere into chaos that reforms. Read only in the ORB_DARK_ELECTRIC branch,
// so Initiative / Electric Light never get the chaos.
uniform float uOrbChaos;
uniform vec3  uBaseColor;
uniform vec3  uGlowColor;
uniform vec3  uShadowColor;
uniform vec3  uHaloColor;
uniform float uBgIntensityMul;
uniform float uHaloRingBase;
uniform float uHaloRingGrowth;
out vec4 fragColor;
float hash21(vec2 p) {
  p = fract(p * vec2(234.34, 435.345));
  p += dot(p, p + 34.23);
  return fract(p.x * p.y);
}
float vnoise(vec2 p) {
  vec2 i = floor(p); vec2 f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  float a = hash21(i);
  float b = hash21(i + vec2(1, 0));
  float c = hash21(i + vec2(0, 1));
  float d = hash21(i + vec2(1, 1));
  return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
}
float fbm(vec2 p) {
  float v = 0.0; float a = 0.5;
  for (int i = 0; i < 3; i++) { v += vnoise(p) * a; p *= 2.0; a *= 0.5; }
  return v;
}
vec3 volumetricBg(vec2 uv, float t) {
  vec3 col = vec3(0.0);
  vec2 p = uv * 2.0;
  for (int i = 0; i < VOLUMETRIC_STEPS; i++) {
    float fi = float(i) / float(VOLUMETRIC_STEPS);
    vec2 sp = p + vec2(t * 0.1, t * 0.05) * (1.0 - fi);
    float n = fbm(sp + vec2(fi * 1.5, 0.0));
    col += uGlowColor * n * 0.06 * (1.0 - fi);
  }
  return col * uBgIntensityMul;
}
vec3 halo(vec2 uv, float kwh) {
  float d = length(uv);
  float ringR = uHaloRingBase + kwh * uHaloRingGrowth;
  float ring = exp(-abs(d - ringR) * 6.0) * 0.45;
  float pulse = 0.6 + 0.4 * sin(iTime * 1.2);
  return uHaloColor * ring * pulse;
}
// Swipe-to-spin rotation matrices (column-major). rotY = yaw about the vertical
// axis (driven by uSpin.x / horizontal swipe); rotX = pitch about the
// horizontal axis (driven by uSpin.y / vertical swipe). Applied to the orb
// camera ray below so the orb spins in 3D following the swipe; both 0 at rest
// give identity, so the silhouette (length(p)) and uKwhScale growth are unchanged.
mat3 rotY(float a) {
  float c = cos(a); float s = sin(a);
  return mat3(c, 0.0, -s,  0.0, 1.0, 0.0,  s, 0.0, c);
}
mat3 rotX(float a) {
  float c = cos(a); float s = sin(a);
  return mat3(1.0, 0.0, 0.0,  0.0, c, s,  0.0, -s, c);
}
#if ORB_KIND == ORB_DARK_ELECTRIC
vec4 sphereOrb(vec2 uv, float t) {
  // Growth recalibration — 2nd iteration (post-runtime walk #2).
  // Lineage: 0.48 + 0.14*kwh (original, 1.29x) → 0.22 + 0.50*kwh
  // (commit 1755615, 3.3x) → 0.20 + 0.75*kwh (this commit, ~4.75x).
  // Warren's runtime feedback: "almost fills at 80%" target. At
  // 80% kWh the orb now hits 0.80 (was 0.62); at 100% it hits
  // 0.95 (under 1.0 — no canvas-edge clip). Starts marginally
  // smaller at 0% (0.20 vs old 0.22). Halo ring widened in
  // lockstep (uHaloRingBase 0.55 + 0.40*kwh on this variant)
  // so it lands at ~0.87 at 80% / ~0.95 at 100% — comfortably
  // outside the orb body, fixing the "halo collapsed onto orb"
  // condition the prior recalibration's commit body flagged.
  float r = 0.20 + uKwhScale * 0.75;
  // Camera distance matched to the smokyOrb path (ro.z = 2.0, used
  // by initiative / electric-light) so dark-electric's orb projects
  // at the SAME on-screen size. The radii were already ~equal
  // (0.80 vs 0.79 at the 80% preview), but this analytic sphere
  // path previously sat the camera farther back (2.5), so the same-
  // size sphere projected ~1.27x smaller — Warren's observed ~45%
  // vs ~60% canvas fill. Apparent silhouette extent goes
  // tan(asin(r/ro.z)) * 1.5: at 2.5 → ~0.507, at 2.0 → ~0.654,
  // matching electric-light's ~0.645. Only apparent size changes;
  // colour / glow / material (uShadowColor + uGlowColor * fres) and
  // the separately-computed halo are untouched.
  vec3 ro = vec3(0.0, 0.0, 2.0);
  vec3 rd = normalize(vec3(uv, -1.5));
  // Swipe-to-spin: orbit the camera ray about the orb centre on both axes.
  // Rotation preserves dot products, so this analytic sphere is unchanged --
  // dark-electric's smooth body reads spin less than the smoky orbs, as noted.
  mat3 spin = rotX(uSpin.y) * rotY(uSpin.x);
  ro = spin * ro;
  rd = spin * rd;
  // Tap-to-chaos (Dark Electric only): a fast high-frequency per-pixel jitter on
  // the ray, scaled by the impulse, fragments the smooth sphere into chaos; as
  // uOrbChaos decays to 0 the jitter vanishes and the orb reforms. Guarded on
  // uOrbChaos > 0 so it is an exact identity no-op at rest -- the baseline
  // sphere, swipe-spin, and uKwhScale growth are untouched.
  if (uOrbChaos > 0.0) {
    vec2 chaosN = vec2(
      sin(uv.x * 34.0 + uv.y * 19.0 + iTime * 47.0),
      cos(uv.y * 28.0 - uv.x * 23.0 + iTime * 41.0)
    );
    rd = normalize(rd + vec3(chaosN, 0.0) * uOrbChaos * 0.5);
  }
  float b = dot(rd, ro);
  float c = dot(ro, ro) - r * r;
  float disc = b * b - c;
  if (disc < 0.0) return vec4(0.0);
  float th = -b - sqrt(disc);
  vec3 p = ro + rd * th;
  vec3 n = normalize(p);
  float fres = pow(1.0 - max(0.0, dot(n, -rd)), 3.0);
  vec3 col = uShadowColor + uGlowColor * fres * 0.4;
  return vec4(col, 1.0);
}
#else
float orbSDF(vec3 p, float t) {
  // Growth recalibration — 2nd iteration (post-runtime walk #2).
  // Lineage: 0.32 + 0.18*kwh (original, 1.56x) → 0.18 + 0.45*kwh
  // (commit 1755615, 3.5x) → 0.15 + 0.80*kwh (this commit,
  // ~6.3x). Warren's "almost fills at 80%" target. At 80% kWh
  // the orb hits 0.79 (was 0.54); at 100% it hits 0.95 (under
  // 1.0 — no canvas-edge clip). Tiny at session start (0.15).
  // Halo ring widened in lockstep (uHaloRingBase 0.50 +
  // 0.45*kwh on these variants) so it lands at ~0.86 at 80% /
  // ~0.95 at 100% — comfortably outside the orb body, fixing
  // the "halo rides the silhouette" condition the prior
  // recalibration's commit body flagged (it noted a ~0.02-unit
  // gap at max; now ~0.16 at 80% tapering to ~0.0 at 100%
  // where both reach 0.95 together — a deliberate fuse-at-full
  // moment, not a collapse). fbm displacement (* 0.04) still
  // adds the smoky surface noise on top.
  float r = 0.15 + uKwhScale * 0.80;
  float n = fbm(p.xy * 3.0 + t * 0.2) * 0.04;
  return length(p) - r - n;
}
vec4 smokyOrb(vec2 uv, float t) {
  vec3 ro = vec3(0.0, 0.0, 2.0);
  vec3 rd = normalize(vec3(uv, -1.5));
  // Swipe-to-spin: orbit the camera ray about the orb centre on both axes, so
  // the surface noise + shading rotate in the swiped direction. length(p) is
  // preserved, so the orb silhouette + uKwhScale growth are untouched.
  mat3 spin = rotX(uSpin.y) * rotY(uSpin.x);
  ro = spin * ro;
  rd = spin * rd;
  float td = 0.0;
  bool hit = false;
  for (int i = 0; i < 48; i++) {
    vec3 p = ro + rd * td;
    float d = orbSDF(p, t);
    if (d < 0.003) { hit = true; break; }
    if (td > 6.0) break;
    td += d * 0.8;
  }
  if (!hit) return vec4(0.0);
  vec3 p = ro + rd * td;
  vec2 e = vec2(0.005, 0.0);
  vec3 n = normalize(vec3(
    orbSDF(p + e.xyy, t) - orbSDF(p - e.xyy, t),
    orbSDF(p + e.yxy, t) - orbSDF(p - e.yxy, t),
    orbSDF(p + e.yyx, t) - orbSDF(p - e.yyx, t)
  ));
  vec3 lightDir = normalize(vec3(0.5, 0.8, 0.2));
  float diff = max(0.0, dot(n, lightDir));
  float fres = pow(1.0 - max(0.0, dot(n, -rd)), 2.5);
  vec3 col = uBaseColor * (diff * 0.6 + 0.2);
  col += uGlowColor * fres * 0.5;
  col += uShadowColor * (1.0 - diff) * 0.3;
  return vec4(col, 1.0);
}
#endif
void main() {
  vec2 uv = (gl_FragCoord.xy * 2.0 - iResolution.xy) / iResolution.y;
  float t = iTime;
  // Swipe-to-spin is applied as a true 3D rotation of the orb camera ray inside
  // smokyOrb / sphereOrb (see the spin matrix there), so a swipe spins the orb
  // on both axes following the finger -- up, down, left, right, or diagonal.
  // uSpin is the zero vec2 at rest (identity), so the baseline iTime motion and
  // the uKwhScale growth stay untouched. The background + halo read raw uv.
  #if ORB_KIND == ORB_ELECTRIC_LIGHT
  // Electric Light is a light-identity theme — its registry entry
  // locks mapStyle to light-v11 for both modes, and the chrome
  // tokens read against white. The shader canvas matches: pure
  // white base, with the volumetricBg noise attenuated on top so
  // it still adds the subtle living motion without dominating.
  // Light-grey orb (uBaseColor ~0.88) reads cleanly on the white
  // background as a soft smoky form per the file's "white glow on
  // pale chrome" identity comment.
  vec3 col = vec3(1.0);
  col += volumetricBg(uv, t) * 0.2;
  #else
  vec3 col = volumetricBg(uv, t);
  #endif
  col += halo(uv, uKwhScale);
  #if ORB_KIND == ORB_DARK_ELECTRIC
  vec4 orbCol = sphereOrb(uv, t);
  #else
  vec4 orbCol = smokyOrb(uv, t);
  #endif
  col = mix(col, orbCol.rgb, orbCol.a);
  col *= uIntensity * 0.5 + 0.6;
  fragColor = vec4(col, 1.0);
}`;

/**
 * Orb shader variant names. The three values match the
 * `Theme.shaderVariant` enum in `packages/skins/src/contract.ts`
 * and the per-variant uniform tuples in `ORB_UNIFORMS` below.
 */
export type OrbVariant = 'initiative' | 'electric-light' | 'dark-electric';

/**
 * Numeric values for the `#define ORB_KIND` injection. These match
 * the literal values in the shader source's `#define ORB_INITIATIVE
 * 0` / `#define ORB_ELECTRIC_LIGHT 1` / `#define ORB_DARK_ELECTRIC
 * 2` declarations at the top of the GLSL string. Changing the
 * source's numeric constants requires changing this table.
 */
export const ORB_KIND_CONSTANTS = {
  initiative: 0,
  'electric-light': 1,
  'dark-electric': 2,
} as const;

/**
 * Per-variant uniform tuples. Values from the ticket's "Per-variant
 * uniforms" block. Tuples are arrays so they can be passed to
 * gl.uniform3fv directly for the vec3 uniforms; the float uniforms
 * are bound via gl.uniform1f.
 *
 * Colour uniforms are in linear-RGB [0, 1] floats. uBgIntensityMul
 * scales the volumetric background so each variant gets a tuned
 * background brightness; uHaloRingBase / uHaloRingGrowth control
 * the halo's distance from the orb centre at zero / max kWh.
 */
export const ORB_UNIFORMS = {
  initiative: {
    uBaseColor: [0.13, 0.76, 0.39],
    uGlowColor: [0.20, 0.95, 0.50],
    uShadowColor: [0.02, 0.10, 0.04],
    uHaloColor: [0.13, 0.76, 0.39],
    uBgIntensityMul: 0.45,
    // Halo widened in lockstep with the orbSDF growth bump (this
    // commit). Was 0.45 + 0.20*kwh (halo at 0.65 max, riding the
    // old orb silhouette). Now 0.50 + 0.45*kwh → halo at ~0.86 at
    // 80% / ~0.95 at 100%, landing just outside the larger orb
    // body (0.79 at 80% / 0.95 at 100%) instead of collapsing onto
    // it.
    uHaloRingBase: 0.50,
    uHaloRingGrowth: 0.45,
  },
  // Greyscale rework (post-C11) — Electric Light and Dark Electric
  // both lost their EVI-green tinted uniforms. New identity: white
  // glow on pale chrome (electric-light) / grey smoke on black
  // (dark-electric). Brand green now appears only on the literal
  // `charging` CSS token (progress fills, session-state UI), not on
  // the shader uniforms. Shape uniforms (uBgIntensityMul,
  // uHaloRingBase, uHaloRingGrowth) intentionally unchanged — only
  // the colour tuples flip.
  'electric-light': {
    uBaseColor: [0.88, 0.88, 0.88],   // light grey smoky orb (was green-tinted)
    uGlowColor: [1.00, 1.00, 1.00],   // pure white glow (was green)
    uShadowColor: [0.55, 0.55, 0.55], // mid-grey shadow (was green-grey)
    uHaloColor: [0.95, 0.95, 0.95],   // near-white halo (was greenish)
    uBgIntensityMul: 0.30,
    // Halo widened in lockstep with the orbSDF growth bump (this
    // commit) — same 0.50 + 0.45*kwh as initiative since both
    // share the orbSDF radius formula (0.15 + 0.80*kwh).
    uHaloRingBase: 0.50,
    uHaloRingGrowth: 0.45,
  },
  'dark-electric': {
    uBaseColor: [0.02, 0.02, 0.02],   // matte black, unchanged
    uGlowColor: [0.70, 0.70, 0.70],   // grey glow (was EVI green)
    uShadowColor: [0.00, 0.00, 0.00], // pure black, unchanged
    uHaloColor: [0.65, 0.65, 0.65],   // grey halo (was EVI green)
    uBgIntensityMul: 0.50,
    // Halo widened in lockstep with the sphereOrb growth bump
    // (this commit). Was 0.62 + 0.18*kwh (halo at 0.76 max, ~0.08
    // gap above the old orb). Now 0.55 + 0.40*kwh → halo at ~0.87
    // at 80% / ~0.95 at 100%, tracking the larger sphere body
    // (0.80 at 80% / 0.95 at 100%). Base dropped 0.62 → 0.55 so
    // the halo isn't floating far off the smaller session-start
    // orb (0.20).
    uHaloRingBase: 0.55,
    uHaloRingGrowth: 0.40,
  },
} as const satisfies Record<OrbVariant, {
  uBaseColor: readonly [number, number, number];
  uGlowColor: readonly [number, number, number];
  uShadowColor: readonly [number, number, number];
  uHaloColor: readonly [number, number, number];
  uBgIntensityMul: number;
  uHaloRingBase: number;
  uHaloRingGrowth: number;
}>;

/**
 * Returns the orb composite shader source with the appropriate
 * `#define ORB_KIND <number>` line injected immediately after the
 * `#version 300 es` directive. GLSL requires `#version` to be the
 * first directive in the source; the inject lands at line 2 so it
 * is in scope for every subsequent `#if ORB_KIND == ...` branch.
 *
 * Called by the WebGL compile pipeline in Commit 6 to produce one
 * compiled program per orb variant. The three variants share the
 * source but each compiles to its own GL program object so the
 * branch predicates collapse at compile time.
 *
 * Commit 11 — optional `volumetricSteps` second argument injects
 * an additional `#define VOLUMETRIC_STEPS <n>` line alongside
 * ORB_KIND when caller wants to override the source's default
 * (32). The shader source guards its own default with an #ifndef
 * block so the injected value wins cleanly without a GLSL macro-
 * redefined warning. ShaderChargingAnimation invokes this with
 * 24 on devices where `navigator.hardwareConcurrency < 4` for
 * ~25% GPU savings on the volumetric pass. Different step counts
 * produce different compiled programs (the loop unrolls under the
 * hood) so the caller must include the step count in the cache
 * key — see glContext.ts's cache-key convention JSDoc.
 */
export function prependOrbKind(
  variant: OrbVariant,
  volumetricSteps?: number,
): string {
  const kind = ORB_KIND_CONSTANTS[variant];
  const lines = ORB_COMPOSITE_SHADER_SOURCE.split('\n');
  const versionIdx = lines.findIndex((line) => line.startsWith('#version'));
  if (versionIdx === -1) {
    // Defensive — should never trip since the source above starts
    // with `#version 300 es`. Returning the raw source here would
    // produce a compile error downstream (no ORB_KIND defined),
    // which is the right failure surface.
    return ORB_COMPOSITE_SHADER_SOURCE;
  }
  const before = lines.slice(0, versionIdx + 1);
  const after = lines.slice(versionIdx + 1);
  const injects = [`#define ORB_KIND ${kind}`];
  if (volumetricSteps !== undefined) {
    injects.push(`#define VOLUMETRIC_STEPS ${volumetricSteps}`);
  }
  return [...before, ...injects, ...after].join('\n');
}
